/**
 * Выбор текста отказа для пользователя.
 *
 * Норма: spec/backend/invariants/api-errors.md
 *  - api-errors-R04 — пользователь видит отказ на языке интерфейса; текст собирает
 *    клиент по машиночитаемому признаку причины и параметрам подстановки.
 *    Серверное сообщение — ЗАПАСНОЙ текст на случай, когда по признаку причины
 *    показать нечего.
 *  - api-errors-R08 — подмена причины обобщённым текстом недопустима.
 *
 * Функция чистая: перевод передаётся снаружи, чтобы её можно было проверить
 * без vue-i18n и чтобы слой отображения не тянулся в модель.
 */

import type { IApiError } from "@/shared/types";

/**
 * Ищет перевод по ключу. Возвращает `undefined`, если ключа нет —
 * тогда выбирается следующий кандидат.
 */
export type TApiErrorTranslate = (
  key: string,
  params: Record<string, unknown>
) => string | undefined;

/** Ключ обобщённого текста — применяется, только когда показать больше нечего. */
export const UNKNOWN_ERROR_TEXT_KEY = "errors.UNKNOWN_ERROR";

/**
 * Суффикс варианта текста для случая, когда параметров подстановки нет.
 *
 * Одна и та же причина (api-errors-R07: одна причина — один признак) приходит
 * то с названным объектом, то без него: продукт не всегда может назвать поле
 * или параметр. Текст с подстановкой в этом случае показал бы пустое место,
 * поэтому у такой причины есть второй, безымянный вариант текста —
 * `errors.<признак>_UNNAMED`. Признак причины при этом остаётся один.
 */
export const UNNAMED_TEXT_SUFFIX = "_UNNAMED";

/**
 * Раздел словаря с подписями полей, которые продукт называет параметром
 * отказа `field` (api-errors-R06): `<зона>.errorFields.<поле>`, затем
 * `errorFields.<поле>`. Продукт называет поле его именем на проводе
 * (`sshPrivateKeyPath`); пользователю показывается подпись поля на языке
 * интерфейса (api-errors-R04, i18n-R02). Подписи нет — имя остаётся как есть.
 */
export const FIELD_LABELS_SECTION = "errorFields";

function withFieldLabel(
  params: Record<string, unknown>,
  translate: TApiErrorTranslate,
  namespaces: readonly string[]
): Record<string, unknown> {
  const field = params["field"];
  if (typeof field !== "string" || field.length === 0) {
    return params;
  }
  const keys = namespaces.map((ns) => `${ns}.${FIELD_LABELS_SECTION}.${field}`);
  keys.push(`${FIELD_LABELS_SECTION}.${field}`);
  for (const key of keys) {
    const label = translate(key, {});
    if (label !== undefined && label.length > 0) {
      return { ...params, field: label };
    }
  }
  return params;
}

/**
 * Собирает ключи-кандидаты для признака причины: сначала зональные
 * (`mapping.errors.NAME_TAKEN`), затем общий (`errors.NAME_TAKEN`).
 */
export function apiErrorTextKeys(
  code: string,
  namespaces: readonly string[]
): readonly string[] {
  const keys = namespaces.map((ns) => `${ns}.errors.${code}`);
  keys.push(`errors.${code}`);
  return keys;
}

/**
 * Возвращает текст отказа на языке интерфейса.
 *
 * Порядок: перевод по признаку причины (с параметрами) → запасное сообщение
 * продукта → обобщённый текст. Признак причины в текст не подставляется:
 * он не является сообщением (api-errors-R07).
 *
 * Отказ без параметров подстановки сначала ищет безымянный вариант текста
 * (`<признак>_UNNAMED`), а если его нет — обычный.
 */
export function resolveApiErrorText(
  error: IApiError | null | undefined,
  translate: TApiErrorTranslate,
  namespaces: readonly string[] = []
): string {
  const fallback = translate(UNKNOWN_ERROR_TEXT_KEY, {}) ?? UNKNOWN_ERROR_TEXT_KEY;

  if (error === null || error === undefined) {
    return fallback;
  }

  const params = withFieldLabel(error.params ?? {}, translate, namespaces);

  if (error.code.length > 0) {
    const codes =
      Object.keys(params).length > 0
        ? [error.code]
        : [`${error.code}${UNNAMED_TEXT_SUFFIX}`, error.code];

    for (const code of codes) {
      for (const key of apiErrorTextKeys(code, namespaces)) {
        const text = translate(key, params);
        if (text !== undefined && text.length > 0) {
          return text;
        }
      }
    }
  }

  if (error.message.length > 0) {
    return error.message;
  }

  return fallback;
}
